import { useMemo } from 'react';
import { useAppSelector } from '@/store/hooks';
import { useFirmDataEnabled } from './useFirmDataEnabled';
import { selectRiaProfile, selectUser } from '@/store/features/userinfo/userinfoSlice';
import { selectApexAccounts } from '@/store/features/apex/apex-selectors';
import { selectPlaidAccounts } from '@/store/features/plaid/plaid-selectors';
import { selectPlaidDisplayBalance } from '@/util/plaid-util';

export type CustodianAccountRef = {
  id: string;
  name: string;
  type: string;
  // Current value of the account, for UI that shows a balance next to the
  // name (the wealth page's account collapse header). `type` may be in
  // either vocabulary depending on the source, so format it with
  // formatAccountType rather than Apex's own accountTypeToName.
  totalEquity: number;
};

export type ResolvedCustodianAccounts = {
  // Whether this firm has Apex enabled at all (legacy per-account pipeline
  // or firm-data-blended), independent of whether the investor actually has
  // an account yet. Mirrors wealth/index.tsx's shouldIncludeApex.
  apexAvailable: boolean;
  // True while the firm-data-enabled signal used to make this decision is
  // still resolving — callers should hold off firing requests / rendering
  // Apex-gated actions until this clears, same as wealth/index.tsx does for
  // its own shouldFetchLegacyApex effects.
  loading: boolean;
  // True when accounts/accountIds are sourced from the legacy per-account
  // Apex Redux pipeline (state.apex.accounts, populated by
  // fetchApexAccounts). False when sourced from firm-data's blended
  // plaidAccounts via custodian_account_id. A firm is always exactly one of
  // the two — see wealth/index.tsx's shouldFetchLegacyApex for the same
  // split.
  isLegacy: boolean;
  // Real Apex/custodian account ids for this investor, however sourced.
  // Use these (never plaidAccounts[].account_id) for calls to
  // fo-apex-backend per-account endpoints (getHoldingsData,
  // getApexAccountAllocation, deposits/withdrawals).
  accountIds: string[];
  // Lightweight display refs for UI that needs to list accounts (e.g. a
  // deposit destination picker) without needing the full ApexAccount shape.
  // Firm-data-sourced entries do not carry Apex-specific fields like
  // holdings, allocation, or IRA contribution/distribution constraints.
  accounts: CustodianAccountRef[];
};

/**
 * Single, correctly-gated way to find out which Apex/custodian accounts this
 * investor has and what ids to use for per-account custodian calls.
 *
 * For a legacy (non-firm-data) Apex firm this is exactly
 * state.apex.accounts, as before. For a firm-data + Apex firm, the legacy
 * per-account Apex Redux pipeline never runs (see wealth/index.tsx's
 * shouldFetchLegacyApex) — firm-data's own blended plaidAccounts list
 * already carries these accounts, each tagged with a `custodian_account_id`
 * once linked to Apex. This hook derives the equivalent account list from
 * that instead, so every consumer (holdings/allocation tabs, the deposit
 * flow) reads from one place rather than reaching into selectApexAccounts
 * directly and silently getting an empty list.
 */
export function useCustodianAccounts(): ResolvedCustodianAccounts {
  const globalRiaProfile = useAppSelector(selectRiaProfile);
  const user = useAppSelector(selectUser);
  const { enabled: firmDataEnabled, loading: firmDataLoading } = useFirmDataEnabled(
    user?.client_id,
  );
  const shouldIncludeApex =
    Boolean(globalRiaProfile.profile?.apex_correspondent_id) &&
    Boolean(globalRiaProfile.features?.apex_enabled);
  const isLegacy = shouldIncludeApex && !firmDataEnabled;

  const legacyAccounts = useAppSelector(selectApexAccounts);
  const plaidAccounts = useAppSelector(selectPlaidAccounts);

  // Every linked account carries a custodian_account_id, not only Apex ones —
  // Plaid-linked accounts are synced into firm-data under their own
  // "PLAID_CASH" custodian record. custodian_code is what actually tells us
  // this account's custodian_account_id is an Apex id fo-apex-backend
  // understands, rather than a Plaid id it would reject.
  const custodianLinkedPlaidAccounts = useMemo(
    () =>
      plaidAccounts.filter(
        (account) => account.custodian_code === 'APEX' && Boolean(account.custodian_account_id),
      ),
    [plaidAccounts],
  );

  return useMemo(() => {
    if (isLegacy) {
      return {
        apexAvailable: shouldIncludeApex && legacyAccounts.length > 0,
        loading: firmDataLoading,
        isLegacy: true,
        accountIds: legacyAccounts.map((account) => account.id),
        accounts: legacyAccounts.map((account) => ({
          id: account.id,
          name: account.name,
          type: account.type,
          totalEquity: account.total_equity,
        })),
      };
    }

    return {
      apexAvailable: shouldIncludeApex && custodianLinkedPlaidAccounts.length > 0,
      loading: firmDataLoading,
      isLegacy: false,
      accountIds: custodianLinkedPlaidAccounts.map(
        (account) => account.custodian_account_id as string,
      ),
      accounts: custodianLinkedPlaidAccounts.map((account) => ({
        id: account.custodian_account_id as string,
        name: account.name,
        type: account.account_type,
        totalEquity: selectPlaidDisplayBalance(account),
      })),
    };
  }, [isLegacy, shouldIncludeApex, legacyAccounts, custodianLinkedPlaidAccounts, firmDataLoading]);
}

export default useCustodianAccounts;
